import { Platform, PLATFORM_TO_HUMAN_READABLE } from 'interfaces/db';
import { SETTING } from 'interfaces/settings';
import { SettingOption, SettingOptionType, SettingSectionConfig } from '../components/SettingSection';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useCallback, useEffect, useMemo } from 'react';
import { setSelectedSectionKey } from 'lib/redux/settings/autodialerSettingsSlice';

export type SettingsConfig = {
    sections: SettingSectionConfig[]
    selectedSection: SettingSectionConfig | null
    updateSelectedSection: (section: SettingSectionConfig | null) => void
}

export function useSettingsConfig(): SettingsConfig {
    const platform = useSelector((state: RootState) => state.autodialerSettings.selectedPlatform)
    const selectedSectionKey = useSelector((state: RootState) => state.autodialerSettings.selectedSectionKey)
    const dispatch = useDispatch()
    const sectionToId = useCallback((section: SettingSectionConfig) => section.section.toLowerCase().replace(/\s+/g, '-'), [])
    const updateSelectedSection = (section: SettingSectionConfig | null) => dispatch(setSelectedSectionKey(section ? sectionToId(section) : null))

    const sections = useMemo(() => {
        if (!platform) return []
        const systemOptions: SettingOption[] = [
                { setting: SETTING.MAX_NUMBER_LINES, type: SettingOptionType.SINGLE },
                { setting: SETTING.MUSIC_WEBSITE, type: SettingOptionType.SINGLE }, 
                { setting: SETTING.TIME_TO_DIAL, type: SettingOptionType.SINGLE },
                { setting: SETTING.TIME_TO_LOG, type: SettingOptionType.SINGLE },
                { setting: SETTING.LINKEDIN_AUTO_OPEN, type: SettingOptionType.SINGLE },
                { setting: SETTING.COMPANY_WEBSITE_AUTO_OPEN, type: SettingOptionType.SINGLE },
                { setting: SETTING.DEFAULT_NUMBER_ROTATION_STRATEGY, type: SettingOptionType.SINGLE }
            ]
        if ([Platform.OUTREACH, Platform.SALESLOFT, Platform.APOLLO, Platform.GONG_ENGAGE].includes(platform)) {
            systemOptions.push({ setting: SETTING.SALESFORCE_URL_AUTO_OPEN, type: SettingOptionType.SINGLE })
        }
        const systemSettings = {
            section: 'System Settings',
            options: systemOptions
        }

        const whoToCallOptions: SettingOption[] = [
            { settings: [SETTING.LOCAL_CALL_TIME_MIN, SETTING.LOCAL_CALL_TIME_MAX], type: SettingOptionType.MULTI, rowLabel: 'Prospect Local Time' },
            { setting: SETTING.VALIDATE_TIMEZONE_VIA_NUMBER, type: SettingOptionType.SINGLE },
            { setting: SETTING.DIALING_MODE, type: SettingOptionType.SINGLE },
        ]
        if (platform === Platform.APOLLO) {
            whoToCallOptions.push({ setting: SETTING.PRIMARY_ALWAYS, type: SettingOptionType.SINGLE })
            whoToCallOptions.push({ setting: SETTING.PRIMARY_FIRST, type: SettingOptionType.SINGLE })
            whoToCallOptions.push({ setting: SETTING.VERIFIED_ALWAYS, type: SettingOptionType.SINGLE })
            whoToCallOptions.push({ setting: SETTING.DNC_ALWAYS, type: SettingOptionType.SINGLE })
        }
        if (platform === Platform.HUBSPOT) {
            whoToCallOptions.push({ setting: SETTING.ALL_RECORDS, type: SettingOptionType.SINGLE })
        }
        whoToCallOptions.push({ setting: SETTING.PHONE_PREFERENCE, type: SettingOptionType.SINGLE })
        const whoToCall = {
            section: 'Who to Call',
            options: whoToCallOptions,
        }
    
        const whenToHangupOptions: SettingOption[] = [
                { settings: [SETTING.HANGUP_RINGING_ACTIVE, SETTING.HANGUP_RINGING_TIME], type: SettingOptionType.MULTI, rowLabel: 'Hangup on Ringing' },
                { settings: [SETTING.HANGUP_VOICEMAIL_ACTIVE, SETTING.HANGUP_VOICEMAIL_TIME], type: SettingOptionType.MULTI, rowLabel: 'Hangup on Voicemail' },
            ]
        const whenToHangup = {
            section: 'When to Hangup',
            options: whenToHangupOptions,
        }
    
        const whatToLogOptions: SettingOption[] = [ { type: SettingOptionType.LOGGING }]
        const whatToLog = {
            section: 'What to Log',
            options: whatToLogOptions,
        }
    
        const automationsOptions: SettingOption[] = [
            // log automations
            { setting: SETTING.NO_AUTO_COMPLETE_TASK, type: SettingOptionType.SINGLE },
            { setting: SETTING.NO_LOG_ONLY, type: SettingOptionType.SINGLE },
            { setting: SETTING.NO_SUMMARY_AUTOMATION, type: SettingOptionType.SINGLE },
            { setting: SETTING.AUTO_PROGRESS_LIVE_ACTIVE, type: SettingOptionType.SINGLE },
            { setting: SETTING.AUTO_PROGRESS_TIME, type: SettingOptionType.SINGLE },
            { setting: SETTING.ACTION_ON_BAD_NUMBER, type: SettingOptionType.SINGLE },
            { setting: SETTING.NO_CANCEL_CALLS, type: SettingOptionType.SINGLE },
            { setting: SETTING.UNIVERSAL_CONTROLLER_ENABLED, type: SettingOptionType.SINGLE },
        ]
        if (platform === Platform.OUTREACH) {
            automationsOptions.push({ setting: SETTING.OUTREACH_MODAL_CLICK_SETTING, type: SettingOptionType.SINGLE })
        }
        if ([Platform.SALESLOFT, Platform.OUTREACH, Platform.APOLLO, Platform.GONG_ENGAGE].includes(platform)) {
            automationsOptions.push({ setting: SETTING.HIDE_WRONG_NUMBER_ELEMENT, type: SettingOptionType.SINGLE })
        }
        if (platform === Platform.VANILLASOFT) {
            automationsOptions.push({ setting: SETTING.AUTOMATIC_EXTENSION_NUMBER, type: SettingOptionType.SINGLE })
        }
        if ([Platform.GONG_ENGAGE, Platform.APOLLO, Platform.VANILLASOFT].includes(platform)) {
            automationsOptions.push({ setting: SETTING.ADD_TRANSCRIPT_TO_SUMMARY_SETTING, type: SettingOptionType.SINGLE })
        }
        if (platform === Platform.GOOGLE_SHEETS) {
            automationsOptions.push({ setting: SETTING.NO_NAVIGATION_ON_CONNECT, type: SettingOptionType.SINGLE })
        }
        const automations = {
            section: 'Automations',
            options: automationsOptions,
        }

        const ringingOptions: SettingOption[] = [
            { setting: SETTING.POWER_CALL_SHOULD_RING, type: SettingOptionType.SINGLE },
            { setting: SETTING.INCOMING_CALL_MUTED, type: SettingOptionType.SINGLE },
            { setting: SETTING.TEAM_CALL_MUTED, type: SettingOptionType.SINGLE },
        ]
        const ringing = {
            section: 'Ringing / Muting',
            options: ringingOptions,
        }

        const sections: SettingSectionConfig[] = [systemSettings, whoToCall, whenToHangup, whatToLog, automations, ringing]
        return sections
    }, [platform])


    useEffect(() => {
        if (sections.length > 0 && (selectedSectionKey === null || !sections.some(section => sectionToId(section) === selectedSectionKey))) {
            updateSelectedSection(sections[0])
        }
    }, [platform, selectedSectionKey, sections])

    const selectedSection = useMemo(() => sections.find(section => sectionToId(section) === selectedSectionKey) || null, [sections, selectedSectionKey])

    return { sections, selectedSection, updateSelectedSection }
} 